/**
 * Zod schema for JSON serialization/deserialization of CadetDoc.
 * This schema mirrors the structure produced by CadetJSONWriter.
 */

import { z } from 'zod';

// #region Base Types

export const IdentifierSchema = z.string().min(1);

export const ElementAttrsSchema = z.record(z.string(), z.unknown());

// Rect schema: [left, top, right, bottom] or "left top right bottom"
export const RectSchema = z.union([
  z.tuple([z.number(), z.number(), z.number(), z.number()]), // Array format
  z.string() // String format
]);

// Path schema: SVG path data string
export const PathSchema = z.string();

// #endregion

// #region Node Types

export const NodeTypeSchema = z.enum(['unit', 'group']);

// #endregion

// #region Class Definitions

export const ElementClassSchema = z.object({
  parent: IdentifierSchema.optional(),
  isAbstract: z.boolean().optional(),
  attrs: ElementAttrsSchema.optional()
});

export const ClassesSchema = z.record(IdentifierSchema, ElementClassSchema).optional();

// #endregion

// #region Graph Structure

export const NodeDataSchema = z.object({
  class: IdentifierSchema,
  attrs: ElementAttrsSchema.optional(),
  parent: IdentifierSchema.optional()
});

export const EdgeDataSchema = z.object({
  class: IdentifierSchema,
  source: IdentifierSchema,
  target: IdentifierSchema,
  attrs: ElementAttrsSchema.optional()
});

export const GraphSchema = z.object({
  nodes: z.record(IdentifierSchema, NodeDataSchema).optional(),
  edges: z.record(IdentifierSchema, EdgeDataSchema).optional()
});

// #endregion

// #region Layout

export const NodeLayoutSchema = z.object({
  bounds: RectSchema
});

export const EdgeLayoutSchema = z.object({
  path: PathSchema
});

export const LayoutSchema = z
  .object({
    nodes: z.record(IdentifierSchema, NodeLayoutSchema).optional(),
    edges: z.record(IdentifierSchema, EdgeLayoutSchema).optional()
  })
  .optional();

// #endregion

// #region Images

export const ImageSourceSchema = z.object({
  type: z.enum(['url', 'file', 'data']),
  url: z.string().optional(),
  file: z.string().optional(),
  data: z.string().optional()
});

export const ImagesSchema = z.record(z.string(), ImageSourceSchema).optional();

// #endregion

// #region Document Schema

export const CadetDocumentSchema = z.object({
  prologue: z.string(),
  includes: z.array(z.string()).optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
  classes: ClassesSchema,
  graph: GraphSchema,
  images: ImagesSchema,
  layout: LayoutSchema
});

export type CadetDocumentType = z.infer<typeof CadetDocumentSchema>;

// #endregion
